/**
 * ManualProcessDialog - Manually process a failed bank transaction
 * 
 * Single Responsibility: Handle manual classification and journal entry creation
 * Interface: Receives transaction data and handlers via props
 */
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Edit3, Loader2 } from 'lucide-react';

// Classification options based on direction
const INFLOW_CLASSIFICATIONS = [
  { value: 'customer_payment', label: 'Customer Payment' },
  { value: 'customer_deposit', label: 'Customer Deposit/Advance' },
  { value: 'loan_received', label: 'Loan Received' },
  { value: 'capital_contribution', label: 'Capital Contribution' },
  { value: 'interest_income', label: 'Interest Income' },
  { value: 'tax_refund', label: 'Tax Refund' },
  { value: 'vendor_refund', label: 'Vendor Refund' },
  { value: 'other_income', label: 'Other Income' }
];

const OUTFLOW_CLASSIFICATIONS = [
  { value: 'bill_payment', label: 'Bill Payment' },
  { value: 'payroll_payment', label: 'Payroll/Salary Payment' },
  { value: 'loan_repayment', label: 'Loan Repayment' },
  { value: 'owner_drawing', label: 'Owner Drawing' },
  { value: 'tax_remittance', label: 'Tax Remittance' },
  { value: 'vat_payment', label: 'VAT Payment' },
  { value: 'bank_charges', label: 'Bank Charges' },
  { value: 'fixed_asset_purchase', label: 'Fixed Asset Purchase' },
  { value: 'other_expense', label: 'Other Expense' }
];

export const ManualProcessDialog = ({
  open,
  onOpenChange,
  transaction,
  onProcess,
  processing = false,
  formatAmount
}) => {
  const [classification, setClassification] = useState('');
  const [description, setDescription] = useState('');

  // Reset form when transaction changes
  useEffect(() => {
    if (transaction) {
      setDescription(transaction.description || '');
      setClassification('');
    }
  }, [transaction]);

  const handleClose = () => {
    setClassification('');
    setDescription('');
    onOpenChange(false);
  };

  const handleProcess = () => {
    if (transaction && classification) {
      onProcess({
        transaction,
        classification,
        description
      });
    }
  };

  const classificationOptions = transaction?.direction === 'inflow' 
    ? INFLOW_CLASSIFICATIONS 
    : OUTFLOW_CLASSIFICATIONS;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit3 className="w-5 h-5 text-[#108981]" />
            Process Transaction Manually
          </DialogTitle>
          <DialogDescription>
            Create a journal entry for this failed transaction
          </DialogDescription>
        </DialogHeader>
        
        {transaction && (
          <div className="space-y-4">
            {/* Transaction Summary */}
            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Amount</span>
                <span className={`font-semibold ${transaction.direction === 'inflow' ? 'text-green-600' : 'text-red-600'}`}>
                  {transaction.direction === 'inflow' ? '+' : '-'}{formatAmount(transaction.amount)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Date</span>
                <span className="text-sm">{transaction.date}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Direction</span>
                <Badge variant="outline" className={transaction.direction === 'inflow' ? 'text-green-600' : 'text-red-600'}>
                  {transaction.direction === 'inflow' ? 'Money In' : 'Money Out'}
                </Badge>
              </div>
              {transaction.counterparty && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Counterparty</span>
                  <span className="text-sm">{transaction.counterparty}</span>
                </div>
              )}
            </div>

            {/* Classification Selection */}
            <div className="space-y-2">
              <Label>Classification *</Label>
              <Select value={classification} onValueChange={setClassification}>
                <SelectTrigger data-testid="manual-classification-select">
                  <SelectValue placeholder="Select transaction type" />
                </SelectTrigger>
                <SelectContent>
                  {classificationOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                  <SelectItem value="internal_transfer">Internal Transfer</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter transaction description"
                rows={2}
                data-testid="manual-description-input"
              />
            </div>

            {/* Error that caused failure */}
            {transaction.error_message && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-xs font-medium text-red-700 mb-1">Original Error:</p>
                <p className="text-xs text-red-600">{transaction.error_message}</p>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={processing}
          >
            Cancel
          </Button>
          <Button
            onClick={handleProcess}
            disabled={!classification || processing}
            className="bg-[#108981] hover:bg-[#0a6b65]"
            data-testid="process-manual-btn"
          >
            {processing && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            Process & Post Journal
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ManualProcessDialog;
